import React from 'react';
import { Trans } from 'react-i18next';
import { differenceInCalendarDays, isSameDay } from 'date-fns';

import { Claim } from '../../../api/types';
import Text from '../../common/Text/Text';
import { formatDate } from '../../parcel-finder/cards/dateHelper';
import { ClaimStatus } from '../../parcel-finder/claims/types';

import styles from './ClaimsTableSubmissionDate.scss';

export type ClaimsTableSubmissionDateProps = {
  claim: Pick<Claim, 'status' | 'submittedAt' | 'createdAt' | 'deadlineLastSubmissionDate'>;
};

function ClaimsTableSubmissionDate({ claim }: ClaimsTableSubmissionDateProps) {
  const { status, submittedAt, createdAt, deadlineLastSubmissionDate } = claim;

  if (!submittedAt && status === ClaimStatus.DECLINED) {
    return <Text ink>{formatDate(createdAt)}</Text>;
  }

  if (!submittedAt && status === ClaimStatus.DRAFT) {
    if (!deadlineLastSubmissionDate) {
      return (
        <Text ink>
          <Trans i18nKey="CLAIMS_LIST_DUE_DEADLINE_TEXT" ink values={{ daysLeft: '?' }} />
        </Text>
      );
    }

    const now = new Date();
    const isDueToday = isSameDay(deadlineLastSubmissionDate, now);
    const daysUntilDeadline = differenceInCalendarDays(deadlineLastSubmissionDate, now);
    const isDueTomorrow = daysUntilDeadline === 1;

    if (isDueToday) {
      return (
        <span className={styles.warning}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TODAY_TEXT" />
        </span>
      );
    }

    if (isDueTomorrow) {
      return (
        <span className={styles.warning}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TOMORROW_TEXT" />
        </span>
      );
    }

    if (now > new Date(deadlineLastSubmissionDate)) {
      return (
        <span className={styles.error}>
          <Trans i18nKey="CLAIMS_LIST_PAST_DEADLINE_DUE_TEXT" />
        </span>
      );
    }

    return (
      <span className={daysUntilDeadline <= 3 ? styles.warning : styles.ink}>
        <Trans i18nKey={`CLAIMS_LIST_DUE_DEADLINE_TEXT`} values={{ daysLeft: daysUntilDeadline }} />
      </span>
    );
  }

  return <Text ink>{formatDate(submittedAt || createdAt)}</Text>;
}

export default React.memo(ClaimsTableSubmissionDate);
